import React from 'react';

import { faChevronLeft, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { RADIAL_BAR_FILLS } from 'src/features/dashboard/utils';
import { useListOccupancyStatistics } from 'src/features/statistics/api/useListOccupancyStatistics';
import StatisticsCards from 'src/features/statistics/components/StatisticsCards';
import StatisticsCardTypeDropdown from 'src/features/statistics/components/StatisticsCardTypeDropdown.tsx';
import { StatisticsOverviewTooltip } from 'src/features/statistics/components/StatisticsOverviewTooltip';
import StatisticsZoneDropdown from 'src/features/statistics/components/StatisticsZoneDropdown.tsx';
import {
    type DurationStatistics,
    type FormattedDurationStatistics,
    OCCUPANCY_REPORT_TYPE,
} from 'src/features/statistics/types';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { tableMessages } from 'src/registry/components/table';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown.tsx';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import { OCCUPANCY_CARD_TYPE } from 'src/features/cards/types';

export default function OccupancyDuration() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const [selectedSubparkings, setSelectedSubparkings] = React.useState<string[]>([]);
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });
    const [cardType, setCardType] = React.useState(OCCUPANCY_CARD_TYPE.NORMAL);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(statisticsMessages.durationStatistics));
        return () => removeTitle();
    }, []);

    const { data: subparkingsData } = useListSubParkings({
        parkingId,
    });

    const formattedSubParkingIds = React.useMemo(() => {
        if (!subparkingsData) return [];
        const subParkingIdsMap = subparkingsData.results.map((res) => res.id);
        return selectedSubparkings.length > 0 ? selectedSubparkings : subParkingIdsMap;
    }, [subparkingsData, selectedSubparkings]);

    const { data: occupancyStatistics, isLoading } = useListOccupancyStatistics({
        parkingId,
        card_type: cardType,
        subparking_ids: formattedSubParkingIds,
        from_date: dateTime.afterDate.toISOString(),
        to_date: dateTime.beforeDate.toISOString(),
    });

    const { durationStatistics, total } = React.useMemo(() => {
        const result: FormattedDurationStatistics = {
            durationStatistics: [],
            total: 0,
        };
        if (!occupancyStatistics) return result;
        const filteredStatistics = occupancyStatistics.results.filter(
            (statistic) => statistic.report_type === OCCUPANCY_REPORT_TYPE.TOTAL,
        );
        result.durationStatistics = filteredStatistics.reduce((statisticAcc: DurationStatistics[], statistic) => {
            if (!statistic.parking_id) {
                result.total = statistic.count_total;
            }
            Object.entries(statistic.count_by_duration).map(
                ([key, value]: [key: string, value: number], durationIndex) => {
                    const durationResult: DurationStatistics = statisticAcc[durationIndex] || {};
                    if (statisticAcc.length <= durationIndex) {
                        durationResult.name = Object.hasOwn(statisticsMessages, key)
                            ? formatMessage(statisticsMessages[key as keyof typeof statisticsMessages])
                            : key;
                    }
                    if (statistic.parking_name) {
                        durationResult[statistic.parking_name] = value;
                    } else durationResult.number = value;
                    statisticAcc[durationIndex]
                        ? (statisticAcc[durationIndex] = durationResult)
                        : statisticAcc.push(durationResult);
                },
            );
            return statisticAcc;
        }, []);
        return result;
    }, [occupancyStatistics]);

    const cardData = React.useMemo(
        () => [
            {
                title: formatMessage(statisticsMessages.totalNumberVisits),
                value: total,
            },
        ],
        [total],
    );

    return (
        <SectionBlock>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/statistics/reports">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(statisticsMessages.backToAllReports)}
                </Link>
            </Button>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.duration)}</SectionBlockTitle>
                    <SectionBlockSubtitle>{formatMessage(statisticsMessages.durationSubtitle)}</SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <StatisticsCards cardData={cardData} />
            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.numberEntriesDurationStay)}</SectionBlockTitle>
                    <div className="flex gap-4">
                        <StatisticsZoneDropdown
                            selectedIds={selectedSubparkings}
                            setSelectedIds={setSelectedSubparkings}
                            data={subparkingsData}
                        />
                        <StatisticsCardTypeDropdown selectedCardType={cardType} setSelectedCardType={setCardType} />
                        <DataTableDateDropdown
                            setValue={setDateTime}
                            inline
                            afterDate={dateTime.afterDate}
                            beforeDate={dateTime.beforeDate}
                        />
                    </div>
                </SectionBlockHeader>
                {total && subparkingsData ? (
                    <SectionBlockBody className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={durationStatistics}>
                                <CartesianGrid vertical={false} strokeOpacity={0.5} />
                                <XAxis dataKey="name" xAxisId={0} />
                                <Legend />
                                {formattedSubParkingIds.map((_, i) => {
                                    return <XAxis dataKey="name" key={i} xAxisId={i + 1} hide />;
                                })}

                                <YAxis tickCount={10} />
                                <Tooltip content={<StatisticsOverviewTooltip />} />
                                {formattedSubParkingIds.length === subparkingsData.results.length && (
                                    <Bar
                                        dataKey="number"
                                        xAxisId={0}
                                        radius={[6, 6, 0, 0]}
                                        name={formatMessage(statisticsMessages.totalNumber)}
                                    />
                                )}
                                {formattedSubParkingIds.map((id, i) => {
                                    const subparking = subparkingsData.results.find(
                                        (subparking) => subparking.id === id,
                                    );
                                    if (!subparking) return null;
                                    return (
                                        <Bar
                                            xAxisId={i + 1}
                                            key={id}
                                            dataKey={subparking.name}
                                            fill={RADIAL_BAR_FILLS[i % RADIAL_BAR_FILLS.length]}
                                            radius={[6, 6, 0, 0]}
                                        />
                                    );
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </SectionBlockBody>
                ) : isLoading ? (
                    <Skeleton className="h-96" />
                ) : (
                    <GeneralBlock className="h-96">
                        <GeneralMessageBlock
                            icon={faSearch}
                            title={formatMessage(tableMessages.noResults)}
                            subtitle={formatMessage(tableMessages.noResultsSubtitleFilters)}
                        />
                    </GeneralBlock>
                )}
            </SectionBlock>
        </SectionBlock>
    );
}
